import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Heart, Users, ArrowRight, Clock, MapPin } from 'lucide-react';

const PRESTATIONS = [
  {
    id: 'rendez-vous',
    icon: <Calendar size={32} />,
    tag: 'Rituel intime',
    title: 'Rendez-vous individuel',
    duration: '15 à 45 min · sur RDV',
    where: 'Boutique Béziers ou visio',
    description:
      "Une consultation olfactive sur-mesure, dédiée à vous seul(e). Nos conseillères identifient votre signature olfactive, vous font découvrir nos gammes, et vous accompagnent dans le choix de votre fragrance idéale.",
    bullets: ['Diagnostic olfactif complet', '15 min offertes avec tout Rituel', 'Disponible en visio'],
    cta: 'Réserver mon créneau',
    link: '/rendez-vous',
    color: '#5C2018',
    bg: 'from-[#FDFAF4] to-white',
  },
  {
    id: 'coaching',
    icon: <Heart size={32} />,
    tag: 'Accompagnement',
    title: 'Coaching olfactif',
    duration: 'Plusieurs séances · sur-mesure',
    where: 'Programme personnalisé',
    description:
      "Pour aller plus loin que le parfum : explorer votre rapport aux odeurs, construire votre identité olfactive, accompagner une transition de vie (mariage, naissance, nouveau poste). Une approche unique en France.",
    bullets: ['Programme sur-mesure', 'Suivi sur plusieurs semaines', 'Outils & créations dédiés'],
    cta: 'Découvrir le coaching',
    link: '/coaching',
    color: '#1E7A6A',
    bg: 'from-[#1E7A6A]/8 to-white',
  },
  {
    id: 'evenements',
    icon: <Users size={32} />,
    tag: 'Expérience collective',
    title: 'Événements & ateliers',
    duration: '1h à 3h · groupes',
    where: 'Bar itinérant chez vous',
    description:
      "EVJF, anniversaires, fêtes entre amis, comités d'entreprise, mariages… Nous nous déplaçons avec notre bar à parfums itinérant dans le lieu que vous réservez. Une expérience originale et inoubliable.",
    bullets: ['Ateliers ludiques et pédagogiques', 'Bar itinérant clé en main', 'Devis sur-mesure'],
    cta: 'Demander un devis',
    link: '/evenements',
    color: '#B8922A',
    bg: 'from-[#B8922A]/8 to-white',
  },
];

const UniversPage = () => {
  return (
    <div className="min-h-screen bg-[#FDFAF4] pb-20">
      {/* HERO */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-[#2E120D] via-[#5C2018] to-[#2E120D] overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url('/assets/rose-filigrane.jpg')", backgroundSize: 'cover' }} />
        <div className="relative max-w-3xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">Au-delà du flacon</p>
          <h1
            className="text-5xl md:text-7xl text-[#FDFAF4] mb-5"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            data-testid="univers-title"
          >
            L&apos;Univers Harmony
          </h1>
          <p className="text-base md:text-lg text-[#FDFAF4]/90 font-light leading-relaxed max-w-2xl mx-auto">
            Chez Harmony, le parfum n&apos;est qu&apos;un départ. Découvrez les trois façons de
            prolonger l&apos;expérience — seul(e), accompagné(e), ou avec ceux que vous aimez.
          </p>
        </div>
      </section>

      {/* 3 CARTES */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {PRESTATIONS.map((p) => (
            <div
              key={p.id}
              id={p.id}
              className={`bg-gradient-to-br ${p.bg} rounded-2xl border-2 p-7 flex flex-col shadow-[0_15px_40px_rgba(92, 32, 24,0.06)] hover:shadow-[0_25px_60px_rgba(92, 32, 24,0.12)] transition-shadow scroll-mt-20`}
              style={{ borderColor: p.color + '40' }}
              data-testid={`univers-card-${p.id}`}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
                style={{ backgroundColor: p.color + '15', color: p.color }}
              >
                {p.icon}
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: p.color }}>{p.tag}</p>
              <h2
                className="text-3xl mb-3"
                style={{ color: p.color, fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                {p.title}
              </h2>
              <div className="flex flex-col gap-1 mb-4 text-xs text-[#332724]/70">
                <span className="flex items-center gap-1.5"><Clock size={12} /> {p.duration}</span>
                <span className="flex items-center gap-1.5"><MapPin size={12} /> {p.where}</span>
              </div>
              <p className="text-sm text-[#332724] font-light leading-relaxed mb-5 flex-1">
                {p.description}
              </p>
              <ul className="space-y-1.5 mb-6">
                {p.bullets.map((b) => (
                  <li key={b} className="text-xs text-[#332724]/80 flex items-start gap-2">
                    <span style={{ color: p.color }}>✦</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={p.link}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-xs uppercase tracking-[0.2em] text-[#FDFAF4] hover:opacity-90 transition-opacity mt-auto"
                style={{ backgroundColor: p.color }}
                data-testid={`univers-cta-${p.id}`}
              >
                {p.cta} <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* PHRASE DE CLÔTURE */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <p
            className="text-2xl md:text-3xl text-[#5C2018] italic mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            « Un parfum n&apos;est jamais seul.
            <br />
            Il porte une histoire, une émotion, un moment partagé. »
          </p>
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A]">
            Maison Harmony · Béziers
          </p>
        </div>
      </section>
    </div>
  );
};

export default UniversPage;
